import { useEffect, useState } from 'react';
import { useStakko } from '../context/StakkoProvider';
import type { StakkoPanelView } from '../types';
import {
  addStakkoTodo,
  deleteStakkoTodo,
  sortStakkoTodos,
  subscribeStakkoTodos,
  toggleStakkoTodo,
} from '../services/todos';
import type { StakkoTodo } from '../services/todos';
import { CheckIcon, PlusIcon, TrashIcon } from './icons';

function formatDate(value: string): string {
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('cs-CZ');
}

export function StakkoTodosPanel({ onNavigate }: { onNavigate: (view: StakkoPanelView) => void }) {
  const { user, firebase } = useStakko();
  const [todos, setTodos] = useState<StakkoTodo[]>([]);
  const [text, setText] = useState('');
  const [subj, setSubj] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      setTodos([]);
      return;
    }
    return subscribeStakkoTodos(firebase.db, user.uid, setTodos);
  }, [user, firebase.db]);

  if (!user) {
    return (
      <div className="stakko-panel-empty">
        <p>Pro osobní TODO se přihlas do Stakko účtu.</p>
        <button type="button" className="stakko-btn stakko-btn-primary" onClick={() => onNavigate('auth')}>
          Přihlásit se
        </button>
      </div>
    );
  }

  const add = async () => {
    const clean = text.trim();
    if (!clean || saving) return;
    setSaving(true);
    try {
      const updated = await addStakkoTodo(firebase.db, user.uid, todos, {
        text: clean,
        subj,
        date,
      });
      setTodos(updated);
      setText('');
      setSubj('');
      setDate('');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (todo: StakkoTodo) => {
    const updated = await toggleStakkoTodo(firebase.db, user.uid, todos, todo.id);
    setTodos(updated);
  };

  const remove = async (todo: StakkoTodo) => {
    const updated = await deleteStakkoTodo(firebase.db, user.uid, todos, todo.id);
    setTodos(updated);
  };

  const open = sortStakkoTodos(todos.filter((todo) => !todo.done));
  const done = sortStakkoTodos(todos.filter((todo) => todo.done));

  const renderTodo = (todo: StakkoTodo) => (
    <li key={todo.id}>
      <button
        type="button"
        className="stakko-todo-check"
        onClick={() => void toggle(todo)}
        title={todo.done ? 'Vrátit mezi úkoly' : 'Splněno'}
        aria-label={todo.done ? 'Vrátit mezi úkoly' : 'Označit jako splněné'}
      >
        <CheckIcon size={14} />
      </button>
      <div className="stakko-todo-body">
        <div className="stakko-todo-title">
          {todo.subj ? <span className="stakko-todo-subj">{todo.subj}</span> : null}
          <span className="stakko-todo-text">{todo.text}</span>
        </div>
        {todo.date ? <span className="stakko-todo-date">{formatDate(todo.date)}</span> : null}
      </div>
      <button
        type="button"
        className="stakko-icon-btn"
        onClick={() => void remove(todo)}
        title="Smazat"
        aria-label="Smazat úkol"
      >
        <TrashIcon size={14} />
      </button>
    </li>
  );

  return (
    <div className="stakko-todos">
      <form
        className="stakko-todos-form"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          className="stakko-todos-text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Co je potřeba udělat?"
          aria-label="Nový úkol"
        />
        <div className="stakko-todos-fields">
          <input
            value={subj}
            onChange={(event) => setSubj(event.target.value)}
            placeholder="Předmět (nepovinné)"
            aria-label="Předmět úkolu"
          />
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            aria-label="Termín úkolu"
            title="Termín"
          />
          <button
            type="submit"
            className="stakko-btn stakko-btn-primary"
            disabled={!text.trim() || saving}
          >
            <PlusIcon size={16} /> Přidat
          </button>
        </div>
      </form>

      {todos.length === 0 ? (
        <p className="stakko-panel-empty">Zatím žádné úkoly. Přidej si první.</p>
      ) : (
        <>
          <p className="stakko-todos-summary">
            {open.length} otevřených · {done.length} splněných
          </p>

          <ul className="stakko-todos-list">{open.map(renderTodo)}</ul>

          {done.length > 0 && (
            <>
              <h3 className="stakko-todos-heading">Splněné</h3>
              <ul className="stakko-todos-list is-done">{done.map(renderTodo)}</ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
